import React, { useState, useRef } from "react";
import Modal from "./Modal";
import Map from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { Autocomplete, TextField, Box } from "@mui/material";
import { issuePropertyOptions } from "../constants/formOptions";
import { ObjectId } from 'bson';

function ModalAddIssue({ name, properties, isOpen, onClose, onDone }) {
  const mapRef = useRef();
  const drawRef = useRef(null);
  const [viewport, setViewport] = useState({
    latitude: 41.8719,
    longitude: 12.56,
    zoom: 10,
  });
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [issueProperties, setIssueProperties] = useState({});
  const debounceTimer = useRef(null);

  const colors = [
    "#F00",
    "#FF9000",
    "#FFFB01",
    "#06E036",
    "#088D25",
    "#0499F6",
    "#0A40AC",
    "#B20DB2",
    "#FB02D2",
    "#494949",
  ];

  const fetchSearchResults = async (term) => {
    if (!term) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search.php?q=${encodeURIComponent(term)}&polygon_geojson=1&format=json&limit=5`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  };

  const handleSearchChange = (event, value) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      fetchSearchResults(value);
    }, 500);
  };

  const handleSelectLocation = (event, selectedOption) => {
    if (selectedOption && selectedOption.boundingbox) {
      const map = mapRef.current?.getMap();
      if (map) {
        const bbox = selectedOption.boundingbox.map(Number);
        map.fitBounds(
          [
            [bbox[2], bbox[0]],
            [bbox[3], bbox[1]],
          ],
          {
            padding: 20,
            duration: 1000,
          }
        );
      }
    }
  };

  const handleMapLoad = (event) => {
      const map = event.target;
  
      if (!drawRef.current) {
        drawRef.current = new MapboxDraw({
          displayControlsDefault: false,
          controls: {
            polygon: true,
            trash: true,
          },
        });
        if (!map.hasControl(drawRef.current)) {
          map.addControl(drawRef.current, "top-right");
          //drawRef.current.changeMode("draw_polygon");
        }
      }
    };

  const handleClose = () => {
    const map = mapRef.current?.getMap();
    if (map && drawRef.current) {
      try { map.removeControl(drawRef.current); } catch(e) {}
      drawRef.current = null;
    }
    resetForm();
    onClose();
  };

  const handleDone = () => {
    let geometry = null;
    if (drawRef.current) {
      try {
        const data = drawRef.current.getAll();
        if (data?.features.length > 0) {
          geometry = data;
        }
      } catch (e) {
        //console.error("Error getting draw data:", e);
      }
    }

    onDone({ _id: new ObjectId(), description, color, geometry, issueProperties });
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setDescription("");
    setColor("");
    try {
      drawRef.current?.deleteAll();
    } catch (e) {}
    setIssueProperties({});
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="large">
      <div className="modal-add-issue">
        <h6>Add a Geohazard</h6>
        <p>Specify the details of the geohazard *</p>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "24px" }}>
          {properties.map((property, index) => (
            <select 
              key={index} 
              name={property.value} 
              value={issueProperties[property.value] || ""} 
              onChange={(e) => 
                setIssueProperties(prev => ({
                  ...prev,
                  [property.value]: e.target.value
                }))
              }
            >
              <option value="" disabled>{property.label}</option>
              {issuePropertyOptions[property.value]?.map((property2) => (
                <option key={property2.value} value={property2.value}>
                  {property2.label}
                </option>
              ))}
            </select>
          ))}
        </div>
        <p>Specify the position of geohazard on map</p>
        <Box style={{ position: "relative", marginBottom: "10px" }}>
          <Autocomplete
            freeSolo
            options={searchResults}
            getOptionLabel={(option) => option.display_name || ""}
            onInputChange={handleSearchChange}
            onChange={handleSelectLocation}
            filterOptions={(options) => options}
            renderInput={(params) => <TextField {...params} label="Search a location" variant="outlined" />}
          />
        </Box>
        <div style={{ height: "276px", width: "100%" }}>
          <Map
            ref={mapRef}
            className="area-map"
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            initialViewState={viewport}
            mapStyle="mapbox://styles/mapbox/streets-v11"
            onMove={(evt) => setViewport(evt.viewState)}
            onLoad={handleMapLoad}
          >
          </Map>
        </div>
        <p>Choose geohazard display color on map *</p>
        <ul className="color-list">
          {colors.map((_color, index) => (
            <li key={index} style={{ backgroundColor: _color }} className={_color===color ? 'selected' : null} onClick={() => setColor(_color)}></li>
          ))}
        </ul>
        <p>Description</p>
        <textarea
          placeholder="less than 100 words"
          className="description"
          onChange={(e) => setDescription(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>
      <div className="modal-actions" style={{ justifyContent: "flex-end" }}>
        <button className="button-outline outline" onClick={handleClose}>
          Cancel
        </button>
        <button className="button-dark" onClick={handleDone}>
          Done
        </button>
      </div>
    </Modal>
  );
}

export default ModalAddIssue;
